import { LookupService } from "@mirage/services/LookupService";
import { Autocomplete, CircularProgress, IconButton, TextField, Tooltip } from "@mui/material";
import { GridColDef, GridSortItem } from "@mui/x-data-grid-premium";
import { IEntityPageRequest } from "@shared/mirage/core/contracts/IEntityPageRequest";
import { Filter } from "@shared/mirage/entityTable/Filter";
import React, { SyntheticEvent, useEffect, useState, useRef, memo, useCallback, useMemo } from "react";
import { LookupFieldProps, LookupSelectedRecord } from "./LookupField";
import { EntityHelper } from "../entityTable/EntityTableHelper";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { MirageTheme } from "@mirage/theme/MuiMirage.module";
import { useBoolean } from "@mirage/hooks/useBoolean";
import { useRecoilValue } from "recoil";
import { currentApplication } from "@mirage/store/currentApplicationStore";
import { SortHelper } from "@shared/mirage/helpers/SortHelper";
import { MetadataRegistry } from "@shared/mirage/metadata/MetaBinding";
import { MirageConstants } from "@shared/mirage/mirageConstants";

interface LookupFieldAsPicklistProps
    extends Pick<
        LookupFieldProps,
        | "relatedRecord"
        | "relatedObject"
        | "onChange"
        | "customPageRequest"
        | "isRequired"
        | "errorMsg"
        | "fetchOnOpen"
        | "readOnly"
        | "filter"
        | "forceFetch"
        | "sortModel"
        | "customColumns"
        | "naturalSort"
        | "placeholder"
        | "leaveOriginalValue"
    > {
    columns: GridColDef[];
    showAllUsersFromApplication?: boolean;
}

const lookupService = new LookupService();

const LookupFieldAsPicklist: React.FC<LookupFieldAsPicklistProps> = memo((props) => {
    const {
        columns,
        relatedObject = "USERS_",
        onChange,
        relatedRecord,
        customPageRequest,
        isRequired,
        errorMsg,
        fetchOnOpen,
        readOnly,
        filter = {} as Filter,
        forceFetch,
        sortModel,
        customColumns,
        showAllUsersFromApplication,
        naturalSort,
        placeholder,
        leaveOriginalValue,
    } = props;
    const [open, setOpen] = useState(false);
    const [options, setOptions] = useState<readonly unknown[]>([]);
    const [loading, setLoading] = useState(false);
    const applicationName = useRecoilValue(currentApplication);
    const initialFetchDoneRef = useRef(false);
    const showAllUsersCache = useRef(false);
    const metadata = MetadataRegistry.get(relatedObject as string);
    const { toggle: toggleShowAllUsers, value: showAllUsers } = useBoolean(false);
    const usersStartAdornmentIcon = showAllUsers ? MirageTheme.icons.userIcon : MirageTheme.icons.addressBook;
    const usersTitle = showAllUsers ? "Show Current Users" : "Show All Users";
    const forceFetchCache = useRef(forceFetch);
    const currentVal = getCurrentVal();
    const firstColumn = useMemo(() => columns[0].field, [columns]);
    const defaultFilterColumns = ["inUse", "active"];
    const defaultFilterColumn = metadata && defaultFilterColumns.find((c) => metadata.columns[c]);

    useEffect(() => {
        if (!open && fetchOnOpen) {
            setOptions([]);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]);

    const showUsersStartAdornmentButton = relatedObject === "USERS_" && !showAllUsersFromApplication;
    const pageRequest = async (pageRequest: IEntityPageRequest) => {
        if (defaultFilterColumn) {
            if (pageRequest.filter.where && Array.isArray(pageRequest.filter.where.AND)) {
                pageRequest.filter.where.AND = [...pageRequest.filter.where.AND, { [defaultFilterColumn]: { equals: 1 } }];
            } else {
                pageRequest.filter.where = { ...pageRequest.filter.where, AND: [{ [defaultFilterColumn]: { equals: 1 } }] };
            }
        }
        if (showAllUsers || showAllUsersFromApplication) {
            return await lookupService.getAllApplicationUsersPageRequest({ applicationName, pageRequest });
        }
        if (customPageRequest) {
            const result = await customPageRequest(pageRequest);
            if (naturalSort) {
                result.records = SortHelper.naturalSortArrayOfObject(result.records, firstColumn);
            }
            return result;
        } else {
            const result = await lookupService.pageRequest({ ...pageRequest, relatedObject });
            if (naturalSort) {
                result.records = SortHelper.naturalSortArrayOfObject(result.records as Record<string, any>[], firstColumn);
            }
            return result;
        }
    };
    useEffect(() => {
        if (open && (!initialFetchDoneRef.current || forceFetchCache.current !== forceFetch || showAllUsersCache.current !== showAllUsers)) {
            setLoading(true);
            const fetchOptions = async () => {
                const select = columns.reduce((o, key) => Object.assign(o, { [key.field]: true }), {});
                const gridSortModel = sortModel ? sortModel : [{ field: firstColumn, sort: "asc" }];
                const order = EntityHelper.prepareSortingObject(gridSortModel as GridSortItem[]);
                const result = await pageRequest({ filter, select, pageSize: 0, offset: 0, order, showAll: true }); // imitate pageRequest
                setOptions(result?.records || []);
                setLoading(false);
                if (!fetchOnOpen) {
                    initialFetchDoneRef.current = true;
                }
            };
            fetchOptions();
            forceFetchCache.current = forceFetch;
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, forceFetch, showAllUsers]);
    const onRecordSelected = async (event: SyntheticEvent<Element, Event>, selectedRecord: LookupSelectedRecord) => {
        if (!onChange) {
            return;
        }
        if (relatedObject === "ATTACHMENT" || relatedObject === "USERS_") {
            onChange({} as React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>, selectedRecord);
            return;
        }
        const record = selectedRecord ? await lookupService.getRecordById({ recordId: selectedRecord.id, relatedObject }) : null;
        if (!record && selectedRecord && leaveOriginalValue) {
            onChange({} as React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>, selectedRecord);
            return;
        }
        onChange({} as React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>, record);
    };
    const showAllUsersOnClick = useCallback(() => {
        showAllUsersCache.current = showAllUsers;
        toggleShowAllUsers();
    }, [showAllUsers, toggleShowAllUsers]);
    const startAdornment = showUsersStartAdornmentButton ? (
        <Tooltip title={usersTitle}>
            <IconButton onClick={showAllUsersOnClick}>
                <FontAwesomeIcon icon={usersStartAdornmentIcon} style={MirageTheme.icons.sizeS} />
            </IconButton>
        </Tooltip>
    ) : null;

    return (
        <Autocomplete
            size="small"
            open={open}
            onOpen={() => setOpen(true)}
            onClose={() => setOpen(false)}
            disabled={!!readOnly}
            value={currentVal}
            className="mirageFormControl__field"
            onChange={(event, value: unknown) => onRecordSelected(event, value as LookupSelectedRecord)}
            isOptionEqualToValue={(option: any, value: any) => option.id === value.id}
            renderOption={(props, option) => {
                const selectedOption = getSelectedOption(option);
                return (
                    <li {...props} key={(option as LookupAsPicklistOption).id as string}>
                        {selectedOption}
                    </li>
                );
            }}
            getOptionLabel={(option) => {
                const selectedOption = getSelectedOption(option);
                return selectedOption as string;
            }}
            options={options}
            loading={loading}
            disableClearable={currentVal ? false : true}
            renderInput={(params) => (
                <TextField
                    {...params}
                    label={placeholder}
                    error={(isRequired && !currentVal) || !!errorMsg}
                    InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                            <React.Fragment>
                                {loading ? <CircularProgress color="inherit" size={20} /> : null}
                                {params.InputProps.endAdornment}
                            </React.Fragment>
                        ),
                        startAdornment,
                    }}
                />
            )}
        />
    );

    function getCurrentVal() {
        if (!relatedRecord?.id) {
            return null;
        }
        const currentObj: Record<string, any> = { id: relatedRecord.id };
        if (customColumns?.length) {
            customColumns.forEach((key) => {
                currentObj[key.field] = relatedRecord[key.field] || "";
                if (metadata?.columns[key.field]?.contentType === "LOOKUP") {
                    const relatedRecordKey = `${key.field}${MirageConstants.RelatedRecordSuffix}`;
                    currentObj[relatedRecordKey] = relatedRecord[relatedRecordKey];
                }
            });
            return currentObj;
        }
        return { ...currentObj, [columns[0].field]: relatedRecord[columns[0].field] || "" };
    }
    function getSelectedOption(option: unknown): string {
        if (!option) {
            return "";
        }
        if (customColumns?.length) {
            return customColumns
                .map((col) => {
                    if (metadata?.columns[col.field]?.contentType === "LOOKUP") {
                        const lookupObject = (option as LookupAsPicklistOption)[`${col.field}${MirageConstants.RelatedRecordSuffix}`];
                        if (!lookupObject) {
                            return "";
                        }
                        return (lookupObject as Record<string, unknown>).name;
                    }
                    return ((option as LookupAsPicklistOption)[col.field] as string) || "";
                })
                .filter(Boolean)
                .join(" - ");
        }
        return ((option as LookupAsPicklistOption)[columns[0].field] as string) || "";
    }
});

type LookupAsPicklistOption = Record<string, unknown>;

export default LookupFieldAsPicklist;
